import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { FileDropZone } from "../components/FileDropZone";
import { MapPin, CheckCircle, XCircle, Clock, File as FileIcon, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
const EMPTY_LINE = () => ({ id: crypto.randomUUID(), description: "", quantity: 1, unit: "ea", unit_cost: "" });
const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function VendorQuoteSubmitPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    vendor_name: "",
    vendor_company: "",
    vendor_phone: "",
    markup_pct: 0,
    vat_enabled: false,
    vat_rate: 20,
    description: "",
    validity_date: "",
    attachments: [],
  });
  const [lines, setLines] = useState([EMPTY_LINE()]);

  useEffect(() => {
    API.get(`/job-requests/vendor-quote/${token}`)
      .then(({ data }) => {
        setData(data);
        if (data.used) setSubmitted(true);
      })
      .catch(err => setError(err.response?.data?.detail || "Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [token]);

  const updateLine = (idx, patch) => setLines(prev => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const base = lines.reduce((s, l) => s + ((Number(l.quantity) || 0) * (Number(l.unit_cost) || 0)), 0);
  const pct = Number(form.markup_pct) || 0;
  const markup = base * (pct / 100);
  const grand = base + markup;
  const vat = form.vat_enabled ? grand * ((Number(form.vat_rate) || 0) / 100) : 0;
  const total = grand + vat;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validLines = lines.filter(l => (l.description || "").trim());
    if (!form.vendor_name.trim()) { toast.error("Your name is required"); return; }
    if (validLines.length === 0) { toast.error("Add at least one quote line"); return; }
    setSubmitting(true);
    try {
      await API.post(`/job-requests/vendor-quote/${token}/submit`, {
        ...form,
        vendor_email: data?.vendor_email,
        markup_pct: Number(form.markup_pct) || 0,
        vat_rate: Number(form.vat_rate) || 0,
        line_items: validLines.map(l => ({
          id: l.id,
          description: l.description.trim(),
          quantity: Number(l.quantity) || 1,
          unit: l.unit || "ea",
          unit_cost: Number(l.unit_cost) || 0,
        })),
      });
      setSubmitted(true);
      toast.success("Quote submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit quote");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="text-center max-w-md">
        <XCircle size={48} className="mx-auto text-red-400 mb-4" />
        <h1 className="text-xl font-bold mb-2">Link Unavailable</h1>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="vendor-quote-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/fixlr-icon.png" alt="Fixlr" className="h-8 w-8 object-contain" />
            <div>
              <h2 className="text-base font-bold font-[Chivo] text-slate-900 dark:text-white leading-none">Fixlr</h2>
              <p className="text-[10px] text-muted-foreground">Property Management</p>
            </div>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{data?.reference}</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {submitted ? (
          <div className="text-center py-16" data-testid="quote-submitted-success">
            <CheckCircle size={56} className="mx-auto text-emerald-500 mb-4" weight="duotone" />
            <h1 className="text-2xl font-bold font-[Chivo] mb-2">Quote Submitted</h1>
            <p className="text-muted-foreground">Thank you! Your quote has been received and added to the job request. The team will review it and be in touch.</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold font-[Chivo] mb-1" data-testid="quote-page-title">Submit Your Quote</h1>
            <p className="text-sm text-muted-foreground mb-6">
              You've been invited to quote on the job below. Build your quote line by line — only your final prices are shared.
            </p>

            {/* Job details */}
            <div className="bg-white dark:bg-slate-900 border border-border rounded-lg p-5 mb-6" data-testid="quote-job-details">
              <h2 className="text-lg font-semibold mb-3">{data?.title}</h2>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {data?.property_address && (
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="text-muted-foreground shrink-0 mt-0.5" />
                    <span>{data.property_address}</span>
                  </div>
                )}
                {data?.category && (
                  <div className="text-muted-foreground">
                    Category: <span className="font-medium text-foreground">{data.category.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                  </div>
                )}
                {data?.priority && (
                  <div className="text-muted-foreground">
                    Priority: <span className="font-medium text-foreground">{data.priority.toUpperCase()}</span>
                  </div>
                )}
                {data?.requested_completion_date && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock size={16} className="shrink-0" />
                    Target: {fmtDate(data.requested_completion_date)}
                  </div>
                )}
              </div>
              {data?.request_details && (
                <div className="mt-4 pt-3 border-t border-border">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">SCOPE OF WORK</p>
                  <p className="text-sm whitespace-pre-wrap">{data.request_details}</p>
                </div>
              )}
            </div>

            {/* Attachments from job */}
            {data?.attachments?.length > 0 && (
              <div className="bg-white dark:bg-slate-900 border border-border rounded-lg p-5 mb-6">
                <p className="text-xs font-semibold text-muted-foreground mb-2">REFERENCE FILES ({data.attachments.length})</p>
                <div className="flex flex-wrap gap-2">
                  {data.attachments.map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-muted px-3 py-2 rounded text-xs hover:bg-muted/80 transition-colors">
                      {a.type?.startsWith("image/") ? (
                        <img src={a.url} alt={a.name} className="h-10 w-10 rounded object-cover" />
                      ) : (
                        <FileIcon size={14} className="text-muted-foreground" />
                      )}
                      <span className="truncate max-w-[140px]">{a.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Quote form */}
            <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 border border-border rounded-lg p-5" data-testid="vendor-quote-form">
              <h3 className="text-sm font-semibold mb-4">Your Details</h3>
              <div className="grid sm:grid-cols-3 gap-4 mb-5">
                <div>
                  <Label>Your Name *</Label>
                  <Input value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} placeholder="e.g. John Smith" required className="mt-1" data-testid="vq-vendor-name" />
                </div>
                <div>
                  <Label>Company Name</Label>
                  <Input value={form.vendor_company} onChange={e => setForm({ ...form, vendor_company: e.target.value })} placeholder="e.g. Smith & Sons Builders" className="mt-1" data-testid="vq-company" />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input value={form.vendor_phone} onChange={e => setForm({ ...form, vendor_phone: e.target.value })} placeholder="e.g. 07700 900123" className="mt-1" data-testid="vq-phone" />
                </div>
              </div>

              {/* Line items */}
              <h3 className="text-sm font-semibold mb-2">Quote Lines *</h3>
              <div className="hidden sm:grid grid-cols-[1fr_64px_64px_100px_90px_32px] gap-2 px-1 mb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                <span>Description</span><span className="text-right">Qty</span><span>Unit</span><span className="text-right">Unit Cost (£)</span><span className="text-right">Total (£)</span><span></span>
              </div>
              <div className="space-y-2 mb-2" data-testid="vq-lines">
                {lines.map((line, idx) => {
                  const lt = (Number(line.quantity) || 0) * (Number(line.unit_cost) || 0);
                  return (
                    <div key={line.id} className="grid grid-cols-2 sm:grid-cols-[1fr_64px_64px_100px_90px_32px] gap-2 items-center border border-border rounded-md p-2 bg-muted/10" data-testid={`vq-line-${idx}`}>
                      <div className="col-span-2 sm:col-span-1">
                        <Input placeholder="e.g. Supply & fit new radiator" value={line.description} onChange={e => updateLine(idx, { description: e.target.value })} data-testid={`vq-line-desc-${idx}`} />
                      </div>
                      <Input type="number" step="any" min="0" value={line.quantity} onChange={e => updateLine(idx, { quantity: e.target.value })} className="text-right" data-testid={`vq-line-qty-${idx}`} />
                      <Input value={line.unit} onChange={e => updateLine(idx, { unit: e.target.value })} placeholder="ea" data-testid={`vq-line-unit-${idx}`} />
                      <Input type="number" step="0.01" min="0" value={line.unit_cost} onChange={e => updateLine(idx, { unit_cost: e.target.value })} placeholder="0.00" className="text-right" data-testid={`vq-line-cost-${idx}`} />
                      <div className="text-right text-sm font-semibold" data-testid={`vq-line-total-${idx}`}>{money(lt)}</div>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)} data-testid={`vq-line-remove-${idx}`}>
                        <Trash size={13} />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <Button type="button" variant="outline" size="sm" className="w-full mb-5" onClick={() => setLines(prev => [...prev, EMPTY_LINE()])} data-testid="vq-add-line">
                <Plus size={14} className="mr-1" /> Add Line
              </Button>

              {/* Markup */}
              <div className="border border-border rounded-md p-3 bg-muted/20 mb-3" data-testid="vq-markup-section">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <Label className="text-sm">Your markup</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Applied on top of your cost prices. Kept private — only your final prices are shared.</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input type="number" min="0" max="500" step="0.5" value={form.markup_pct} onChange={e => setForm({ ...form, markup_pct: e.target.value })} className="w-20 h-8" data-testid="vq-markup-pct" />
                    <span className="text-xs">%</span>
                  </div>
                </div>
              </div>

              {/* VAT + totals */}
              <div className="border border-border rounded-md p-3 bg-muted/20 mb-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!form.vat_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, vat_enabled: v }))} data-testid="vq-vat-toggle" />
                    <Label className="text-sm cursor-pointer">Charge VAT</Label>
                  </div>
                  {form.vat_enabled && (
                    <div className="flex items-center gap-1.5">
                      <Input type="number" min="0" max="100" step="0.5" value={form.vat_rate} onChange={e => setForm({ ...form, vat_rate: e.target.value })} className="w-20 h-8" data-testid="vq-vat-rate" />
                      <span className="text-xs">%</span>
                    </div>
                  )}
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Your cost subtotal</span><span>{money(base)}</span></div>
                  {pct > 0 && (
                    <>
                      <div className="flex justify-between"><span className="text-muted-foreground">Markup ({pct}%)</span><span data-testid="vq-markup-amount">+{money(markup)}</span></div>
                      <div className="flex justify-between font-medium"><span className="text-muted-foreground">Quoted subtotal</span><span data-testid="vq-quoted-subtotal">{money(grand)}</span></div>
                    </>
                  )}
                  {form.vat_enabled && <div className="flex justify-between"><span className="text-muted-foreground">VAT</span><span>{money(vat)}</span></div>}
                  <div className="flex justify-between font-bold pt-1 border-t border-border"><span>Total</span><span data-testid="vq-total">{money(total)}</span></div>
                </div>
              </div>

              <div className="mb-4">
                <Label>Valid Until</Label>
                <DateInput value={form.validity_date} onChange={e => setForm({ ...form, validity_date: e.target.value })} className="mt-1 max-w-[200px]" data-testid="vq-validity" />
              </div>

              <div className="mb-4">
                <Label>Notes / Exclusions</Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Timeline, materials assumptions, exclusions, access requirements..."
                  className="mt-1"
                  data-testid="vq-description"
                />
              </div>

              <div className="mb-6">
                <Label>Supporting Documents</Label>
                <p className="text-[10px] text-muted-foreground mb-1">Attach itemised pricing, insurance certs, or photos of similar work</p>
                <FileDropZone
                  files={form.attachments}
                  onFilesChange={(files) => setForm(prev => ({ ...prev, attachments: files }))}
                  folder="vendor-quotes"
                  accept="all"
                  maxFiles={10}
                  compact
                />
              </div>

              <Button type="submit" className="w-full h-11 bg-blue-700 hover:bg-blue-800" disabled={submitting} data-testid="vq-submit-btn">
                {submitting ? "Submitting..." : `Submit Quote — ${money(total)}`}
              </Button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t border-border text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <img src="/fixlr-icon.png" alt="Fixlr" className="h-5 w-5 object-contain opacity-60" />
                <span className="text-xs text-muted-foreground font-[Chivo] font-semibold">Fixlr Ltd</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Property Management Platform</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
